import React, { createContext, useContext, useMemo } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IAllFeatureIdsQuery, useAllFeatureIdsQuery } from 'generated/sanity-graphql';

interface IFeatureIds {
  featureAccountId?: string;
  featureAccountDeletePageId?: string;
  featureAccountFormId?: string;
  featureAccountRequestInfoPageId?: string;
  featureDisclaimersId?: string;
  featureFooterId?: string;
  featureGeolocationModalId?: string;
  featureGiftId?: string;
  featureHomePageId?: string;
  featureKioskId?: string;
  featureLayoutId?: string;
  featureMenuId?: string;
  featureNavigationId?: string;
  featureOffersId?: string;
  featureSupportDataId?: string;
  featureStaticMenuId?: string;
  featureCateringPageId?: string;
  featureLoyaltyUIId?: string;
  featureLocationPromoId?: string;
  featureNutritionId?: string;
  // add new feature ids here
}

export interface IFeaturesContext extends IFeatureIds {
  featureIdsLoading: boolean;
}

export const FeaturesContext = createContext<IFeaturesContext>({
  featureIdsLoading: true
});

export const useFeaturesContext = () => useContext(FeaturesContext);

const processAllFeatureIds = (data: IAllFeatureIdsQuery | undefined): IFeatureIds => {
  if (!data) {
    return {};
  }

  return {
    featureAccountId: data?.allFeatureAccounts?.[0]?._id,
    featureAccountDeletePageId: data?.allFeatureAccountDeletePages?.[0]?._id,
    featureAccountFormId: data?.allFeatureAccountForms?.[0]?._id,
    featureAccountRequestInfoPageId: data?.allFeatureAccountRequestInfoPages?.[0]?._id,
    featureDisclaimersId: data?.allFeatureDisclaimers?.[0]?._id,
    featureFooterId: data?.allFeatureFooters?.[0]?._id,
    featureGeolocationModalId: data?.allFeatureGeolocationModals?.[0]?._id,
    featureGiftId: data?.allFeatureGifts?.[0]?._id,
    featureHomePageId: data?.allFeatureHomePages?.[0]?._id,
    featureLoyaltyUIId: data?.allLoyaltyUis?.[0]?._id,
    featureKioskId: data?.allFeatureKiosks?.[0]?._id,
    featureLayoutId: data?.allFeatureLayouts?.[0]?._id,
    featureMenuId: data?.allFeatureMenus?.[0]?._id,
    featureNavigationId: data?.allFeatureNavigations?.[0]?._id,
    featureNutritionId: data?.allFeatureNutritions?.[0]?._id,
    featureOffersId: data?.allFeatureOffers?.[0]?._id,
    featureSupportDataId: data?.allSupportData?.[0]?._id,
    featureStaticMenuId: data?.allFeatureStaticMenus?.[0]?._id,
    featureCateringPageId: data?.allFeatureCateringPages?.[0]?._id,
    featureLocationPromoId: data?.allFeatureLocationPromos?.[0]?._id
  };
};

export const FeaturesProvider: React.FC<IBaseProps> = ({ children }) => {
  const { data: allFeatureIds, loading: featureIdsLoading } = useAllFeatureIdsQuery();

  const contextValue = useMemo(() => {
    const featureIds = processAllFeatureIds(allFeatureIds);
    return { ...featureIds, featureIdsLoading };
  }, [allFeatureIds, featureIdsLoading]);

  return <FeaturesContext.Provider value={contextValue}>{children}</FeaturesContext.Provider>;
};
